import React, { useState, useEffect, useRef } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Paper,
  Tab,
  Tabs,
} from "@material-ui/core";
import {
  ExpandMore,
  Add,
  Delete,
  Settings,
  Code,
  Build,
} from "@material-ui/icons";
import { MdOutlineExtension } from "react-icons/md";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  multFieldLine: {
    display: "flex",
    gap: theme.spacing(1),
    "& > *": {
      flex: 1,
    },
  },
  sectionTitle: {
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  tabPanel: {
    padding: theme.spacing(2),
  },
  paramItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(0.5),
  },
  accordion: {
    marginBottom: theme.spacing(1),
    "&:before": {
      display: "none",
    },
  },
  accordionSummary: {
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
    borderRadius: theme.spacing(0.5),
    minHeight: 48,
    "&.Mui-expanded": {
      minHeight: 48,
    },
  },
  accordionDetails: {
    padding: theme.spacing(2),
    flexDirection: "column",
  },
  serverPreset: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5),
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  serverPresetSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(0, 0, 0, 0.02)",
  },
}));

// Presets de servidores MCP populares
const serverPresets = [
  {
    id: "filesystem",
    name: "Filesystem",
    description: "Acesso a arquivos do sistema",
    serverType: "stdio",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-filesystem", "/path/to/allowed"],
    tools: ["read_file", "write_file", "list_directory", "create_directory"],
  },
  {
    id: "postgres",
    name: "PostgreSQL",
    description: "Consultas ao banco PostgreSQL",
    serverType: "stdio",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-postgres", "postgresql://..."],
    tools: ["query", "list_tables", "describe_table"],
  },
  {
    id: "brave-search",
    name: "Brave Search",
    description: "Pesquisas na web via Brave",
    serverType: "stdio",
    command: "npx",
    args: ["-y", "@anthropic/mcp-server-brave-search"],
    tools: ["brave_search"],
  },
  {
    id: "github",
    name: "GitHub",
    description: "Interação com repositórios GitHub",
    serverType: "stdio",
    command: "npx",
    args: ["-y", "@modelcontextprotocol/server-github"],
    tools: [
      "create_issue",
      "list_issues",
      "create_pull_request",
      "search_code",
    ],
  },
  {
    id: "custom",
    name: "Personalizado",
    description: "Configurar servidor customizado",
    serverType: "stdio",
    command: "",
    args: [],
    tools: [],
  },
];

const FlowBuilderAddMCPClientModal = ({
  open,
  onSave,
  data,
  onUpdate,
  close,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const initialState = {
    serverName: "",
    serverType: "stdio", // stdio, sse, websocket
    command: "",
    args: [],
    envVars: {},
    toolName: "",
    toolParams: [],
    outputVariable: "mcpResult",
    timeout: 30000,
  };

  const [mcpConfig, setMcpConfig] = useState(initialState);
  const [labels, setLabels] = useState({
    title: "Adicionar Cliente MCP ao fluxo",
    btn: "Adicionar",
  });
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [newArg, setNewArg] = useState("");
  const [newEnvKey, setNewEnvKey] = useState("");
  const [newEnvValue, setNewEnvValue] = useState("");
  const [newParamKey, setNewParamKey] = useState("");
  const [newParamValue, setNewParamValue] = useState("");

  useEffect(() => {
    if (open === "edit") {
      setLabels({
        title: "Editar Cliente MCP",
        btn: "Salvar",
      });
      const config = data?.data?.mcpConfig || {};
      setMcpConfig({
        ...initialState,
        ...config,
      });
    } else if (open === "create") {
      setLabels({
        title: "Adicionar Cliente MCP ao fluxo",
        btn: "Adicionar",
      });
      setMcpConfig(initialState);
      setSelectedPreset(null);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    setActiveTab(0);
    setNewArg("");
    setNewEnvKey("");
    setNewEnvValue("");
    setNewParamKey("");
    setNewParamValue("");
    close(null);
  };

  const handleSave = () => {
    if (!mcpConfig.serverName || !mcpConfig.command) {
      return;
    }

    if (open === "edit") {
      onUpdate({
        ...data,
        data: { mcpConfig },
      });
    } else if (open === "create") {
      onSave({ mcpConfig });
    }
    handleClose();
  };

  const selectPreset = (preset) => {
    setSelectedPreset(preset.id);
    if (preset.id !== "custom") {
      setMcpConfig({
        ...mcpConfig,
        serverName: preset.name,
        serverType: preset.serverType,
        command: preset.command,
        args: [...preset.args],
      });
    } else {
      setMcpConfig({
        ...initialState,
        serverName: "Servidor Personalizado",
      });
    }
  };

  const addArg = () => {
    if (newArg.trim()) {
      setMcpConfig({
        ...mcpConfig,
        args: [...mcpConfig.args, newArg.trim()],
      });
      setNewArg("");
    }
  };

  const removeArg = (index) => {
    setMcpConfig({
      ...mcpConfig,
      args: mcpConfig.args.filter((_, i) => i !== index),
    });
  };

  const addEnvVar = () => {
    if (newEnvKey.trim()) {
      setMcpConfig({
        ...mcpConfig,
        envVars: {
          ...mcpConfig.envVars,
          [newEnvKey.trim()]: newEnvValue,
        },
      });
      setNewEnvKey("");
      setNewEnvValue("");
    }
  };

  const removeEnvVar = (key) => {
    const newEnvVars = { ...mcpConfig.envVars };
    delete newEnvVars[key];
    setMcpConfig({
      ...mcpConfig,
      envVars: newEnvVars,
    });
  };

  const addToolParam = () => {
    if (newParamKey.trim()) {
      setMcpConfig({
        ...mcpConfig,
        toolParams: [
          ...mcpConfig.toolParams,
          { key: newParamKey.trim(), value: newParamValue },
        ],
      });
      setNewParamKey("");
      setNewParamValue("");
    }
  };

  const removeToolParam = (index) => {
    setMcpConfig({
      ...mcpConfig,
      toolParams: mcpConfig.toolParams.filter((_, i) => i !== index),
    });
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle>{labels.title}</DialogTitle>
        <DialogContent dividers>
          {/* TABS */}
          <Paper square style={{ marginBottom: 16 }}>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab label="Servidor" icon={<Settings />} />
              <Tab label="Ferramenta" icon={<Build />} />
              <Tab label="Avançado" icon={<Code />} />
            </Tabs>
          </Paper>

          {/* TAB 0: Servidor */}
          {activeTab === 0 && (
            <div className={classes.tabPanel}>
              <Typography variant="subtitle1" gutterBottom>
                Selecione um preset ou configure manualmente
              </Typography>

              <Box
                display="flex"
                gap={2}
                flexWrap="wrap"
                mb={3}
                style={{ marginTop: 16 }}
              >
                {serverPresets.map((preset) => (
                  <Box
                    key={preset.id}
                    className={`${classes.serverPreset} ${
                      selectedPreset === preset.id
                        ? classes.serverPresetSelected
                        : ""
                    }`}
                    onClick={() => selectPreset(preset)}
                    style={{ flex: "1 1 200px", maxWidth: 250 }}
                  >
                    <Typography variant="subtitle2" style={{ fontWeight: "bold" }}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {preset.description}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <TextField
                label="Nome do Servidor"
                value={mcpConfig.serverName}
                onChange={(e) =>
                  setMcpConfig({ ...mcpConfig, serverName: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                required
              />

              <FormControl fullWidth margin="dense" variant="outlined">
                <InputLabel>Tipo de Servidor</InputLabel>
                <Select
                  label="Tipo de Servidor"
                  value={mcpConfig.serverType}
                  onChange={(e) =>
                    setMcpConfig({ ...mcpConfig, serverType: e.target.value })
                  }
                >
                  <MenuItem value="stdio">STDIO (linha de comando)</MenuItem>
                  <MenuItem value="sse">SSE (Server-Sent Events)</MenuItem>
                  <MenuItem value="websocket">WebSocket</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Comando"
                value={mcpConfig.command}
                onChange={(e) =>
                  setMcpConfig({ ...mcpConfig, command: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                required
                placeholder="npx, node, python, etc."
              />

              {/* Args */}
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Argumentos
                </Typography>
                <Box display="flex" gap={1} alignItems="center" mb={1}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Adicionar argumento"
                    value={newArg}
                    onChange={(e) => setNewArg(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addArg();
                      }
                    }}
                    style={{ flex: 1 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addArg}
                    disabled={!newArg.trim()}
                  >
                    Adicionar
                  </Button>
                </Box>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {mcpConfig.args.map((arg, index) => (
                    <Chip
                      key={index}
                      label={arg}
                      onDelete={() => removeArg(index)}
                      deleteIcon={<Delete />}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            </div>
          )}

          {/* TAB 1: Ferramenta */}
          {activeTab === 1 && (
            <div className={classes.tabPanel}>
              <TextField
                label="Nome da Ferramenta"
                value={mcpConfig.toolName}
                onChange={(e) =>
                  setMcpConfig({ ...mcpConfig, toolName: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                placeholder="read_file, query, search, etc."
                helperText="Nome da ferramenta MCP a ser executada"
              />

              {/* Tool Params */}
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Parâmetros da Ferramenta
                </Typography>
                <Box display="flex" gap={1} alignItems="center" mb={1}>
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Nome"
                    value={newParamKey}
                    onChange={(e) => setNewParamKey(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Valor (use ${variavel})"
                    value={newParamValue}
                    onChange={(e) => setNewParamValue(e.target.value)}
                    style={{ flex: 2 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Add />}
                    onClick={addToolParam}
                    disabled={!newParamKey.trim()}
                  >
                    Adicionar
                  </Button>
                </Box>
                {mcpConfig.toolParams.map((param, index) => (
                  <div key={index} className={classes.paramItem}>
                    <Typography variant="body2" style={{ flex: 1 }}>
                      <strong>{param.key}:</strong> {param.value}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => removeToolParam(index)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </div>
                ))}
              </Box>

              <TextField
                label="Variável de Saída"
                value={mcpConfig.outputVariable}
                onChange={(e) =>
                  setMcpConfig({ ...mcpConfig, outputVariable: e.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                helperText="Nome da variável onde o resultado será armazenado"
              />
            </div>
          )}

          {/* TAB 2: Avançado */}
          {activeTab === 2 && (
            <div className={classes.tabPanel}>
              <TextField
                label="Timeout (ms)"
                value={mcpConfig.timeout}
                onChange={(e) =>
                  setMcpConfig({
                    ...mcpConfig,
                    timeout: parseInt(e.target.value) || 30000,
                  })
                }
                variant="outlined"
                margin="dense"
                fullWidth
                type="number"
                helperText="Tempo máximo de espera pela resposta"
              />

              {/* Env Vars */}
              <Accordion className={classes.accordion}>
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  className={classes.accordionSummary}
                >
                  <Typography className={classes.sectionTitle}>
                    <Code />
                    Variáveis de Ambiente
                  </Typography>
                </AccordionSummary>
                <AccordionDetails className={classes.accordionDetails}>
                  <Box display="flex" gap={1} alignItems="center" mb={1}>
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="NOME"
                      value={newEnvKey}
                      onChange={(e) =>
                        setNewEnvKey(e.target.value.toUpperCase())
                      }
                      style={{ flex: 1 }}
                    />
                    <TextField
                      variant="outlined"
                      size="small"
                      placeholder="valor"
                      value={newEnvValue}
                      onChange={(e) => setNewEnvValue(e.target.value)}
                      style={{ flex: 2 }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Add />}
                      onClick={addEnvVar}
                      disabled={!newEnvKey.trim()}
                    >
                      Adicionar
                    </Button>
                  </Box>
                  {Object.entries(mcpConfig.envVars).map(([key, value]) => (
                    <div key={key} className={classes.paramItem}>
                      <Typography variant="body2" style={{ flex: 1 }}>
                        <strong>{key}:</strong> {value}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeEnvVar(key)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </AccordionDetails>
              </Accordion>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!mcpConfig.serverName || !mcpConfig.command}
          >
            {labels.btn}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddMCPClientModal;
